<template>
  <div class="object-dashboard">
    <v-app-bar
        app
        clipped-right
        class="mt-16 sub-app-bar px-1"
        fixed
    >
      <v-row align="center">
        <v-col cols="1" sm="4" md="4">
          <div class="d-flex flex-row">
            <div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs}">
                  <v-btn icon @click="$router.push(`/${$store.state.manager.bookModuleUrl}/home`)" v-bind="attrs" v-on="on">
                    <v-icon>arrow_back_ios</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('buttons:back')}}</span>
              </v-tooltip>
            </div>
            <div v-if="$vuetify.breakpoint.smAndUp">
              <p class="title">{{ $t('book/process:overview.sub-bar-title') }}</p>
              <p class="subtitle">{{ $t('common:dashboard') }}</p>
            </div>
          </div>
        </v-col>

        <!-- Current Date -->
        <v-col class="d-none d-sm-flex"></v-col>

        <v-col cols="1" sm="3" md="2" xl="1" class="text-right d-none d-sm-flex">
          <p class="mb-0 pb-0 grey--text ml-auto">
            <v-icon color="grey">calendar_today</v-icon>
            {{ currentDate }}
          </p>
        </v-col>
      </v-row>
    </v-app-bar>
    <v-row align="center" class="pa-5 px-md-10 pt-md-10">

      <v-col :lg="cardSize" xl="2">
        <ProgressCard
            icon="apartment"
            :title="$t('book/process:dashboard.progress-card.text-terminated')"
            :progressValue="100"
            :infoText="countFinished"
            :btnText="$t('book/process:dashboard.progress-card.show-processes')"
            @onClick="$refs.taskList.filterBy = 'finishedProcess'"
            @clickedCard="$refs.taskList.filterBy = 'finishedProcess'"
            showInfoText
        />
      </v-col>
      <v-col :lg="cardSize" xl="2">
        <ProgressCard
            icon="apartment"
            :title="$t('book/process:dashboard.progress-card.text-open')"
            :progressValue="100"
            :infoText="countOpen"
            :btnText="$t('book/process:dashboard.progress-card.show-processes')"
            @onClick="$refs.taskList.filterBy = 'assignedProcess'"
            @clickedCard="$refs.taskList.filterBy = 'assignedProcess'"
            showInfoText
        />
      </v-col>
      <v-col :lg="cardSize" xl="2">
        <ProgressCard
            icon="apartment"
            :title="$t('book/process:dashboard.progress-card.text-invitations')"
            :progressValue="100"
            :infoText="countInvitations"
            :btnText="$t('book/process:dashboard.progress-card.show-invitations')"
            @onClick="$refs.taskList.filterBy = 'allInvitations'"
            @clickedCard="$refs.taskList.filterBy = 'allInvitations'"
            showInfoText
        />
      </v-col>
      <v-col :lg="cardSize" xl="2">
        <ProgressCard
            icon="apartment"
            :title="$t('book/process:dashboard.progress-card.text-thisWeek')"
            :progressValue="100"
            :infoText="countThisWeek"
            :btnText="$t('book/process:dashboard.progress-card.show-processes')"
            @onClick="$refs.taskList.filterBy = 'thisWeek'"
            @clickedCard="$refs.taskList.filterBy = 'thisWeek'"
            showInfoText
        />
      </v-col>
    </v-row>
    <TaskList
        class="pa-5 px-md-10"
        ref="taskList"
        :headerTitle="$t('common:task-list')"
        :headerBtnText="$t('book/process:dashboard.task-list.header-btn')"
        @clickedCard="goToTask"
        @updateData="updateTaskList"
        @acceptTask="acceptInvitation"
        @declineTask="declineInvitation"
        @startTask="showGPSPermission($event)"
        :sorts="taskListSorts"
        :filter="taskListFilter"
        defaultDesc
        defaultSortBy="finishedOn"
        defaultFilterBy="all"
        :hideUser="isAdmin"
        hideHeaderBtn
        enable-facility-name-btn
        @clickedFacilityName="showFacility"
    />

    <TwoButtonDialog
        ref="checkGPSPermission"
        :hide-button="true"
        :dialog-title="$t('common:gps-control')"
        :dialog-text="$t('book/process:dashboard.dialog')"
        :button1-text="$t('buttons:accept')"
        :button2-text="$t('buttons:decline')"
        @clickedButton1="checkUserLocation($event)"
        @clickedButton2="startProcessJob($event, false)"
    />

    <ProcessResultDialog ref="processResultDialog"/>
    <ProcessShowDialog ref="processShowDialog" />
    <ProcessQuickLinkSidebar v-if="!isWorker" ref="quickLinkSidebar"/>
    <ExternalFacilityDialog ref="facilityDialog"/>
  </div>
</template>

<script>

import TaskListService from '@/modules/book/services/process/impl/task-list-service'
import FacilityService from "@/modules/book/services/facility-service";
import FacilityUserAssignmentService from "@/modules/book/services/facility-user-assignment-service";
import ProcessJobService from "@/modules/book/services/process/impl/process-job-service";

import Utils from "@/modules/core/services/utils";
import GPSUtils from "@/modules/core/services/gps-utils";
import DataUtils from "@/modules/core/services/data-utils";

import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import Facility from "@/modules/book/data/facility";
import ProcessQuickLinkSidebar from "@/modules/book/views/process/ProcessQuickLinkSidebar"
import ProgressCard from "@/modules/core/components/ProgressCard/ProgressCard";
import TaskList from "@/modules/book/components/TaskList/TaskList"
import ProcessResultDialog from "@/modules/book/components/Process/ProcessResultDialog"
import ProcessShowDialog from "@/modules/book/components/Process/ProcessShowDialog"

import {mapState} from 'vuex'
import {Role} from "@/modules/core/data/roles";
import TaskListUtils from "@/modules/book/services/task-list-utils";
import DateUtils from "@/modules/core/services/date-utils";
import ExternalFacilityDialog from "@/modules/book/components/Facility/ExternalFacilityDialog";

export default {
  name: "DashboardWorker",
  components: {
    ExternalFacilityDialog,
    TwoButtonDialog,
    ProcessQuickLinkSidebar,
    ProgressCard,
    TaskList,
    ProcessResultDialog,
    ProcessShowDialog
  },
  data() {
    return {
      taskList: {
        options: [],
        search: [],
        filter: null,
        period: null
      },

      acceptedGPSCheck: false,
      currentDate: null,

      countFinished: null,
      countOpen: null,
      countInvitations: null,
      countThisWeek: null
    }
  },
  computed: {
    ...mapState('book/layout', {
      rightProcessDrawer: state => state.rightProcessDrawer

    }),
    cardSize() {
      // if both sidebars are fully opened, make card size bigger
      if(this.rightProcessDrawer) {
        return '6'
        // if both side bars are closed, make card size smaller
      } else {
        return '4'
      }
    },

    taskListSorts() {
      return [
        {label: this.$t('common:date'), value: "finishedOn"},
        {label: this.$t('book/process:dashboard.task-list.sorts.process-name'), value: "processName"},
        {label: this.$t('book/process:dashboard.task-list.sorts.facility-name'), value: "facilityName"},
      ]
    },

    taskListFilter() {
      return [
        {label: this.$t('common:all'), value: 'all'},
        {label: this.$t('book/process:dashboard.task-list.filter.all-invitations'), value: 'allInvitations'},
        {label: this.$t('book/process:dashboard.task-list.filter.process-invitations'), value: 'processInvitations'},
        {label: this.$t('book/process:dashboard.task-list.filter.role-invitations'), value: 'roleInvitations'},
        {label: this.$t('book/process:dashboard.task-list.filter.assigned'), value: 'assignedProcess'},
        {label: this.$t('book/process:dashboard.task-list.filter.finished'), value: 'finishedProcess'},
        {label: this.$t('book/process:dashboard.task-list.filter.week'), value: 'thisWeek'},
      ]
    },
    isAdmin() {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.includes(Role.Admin);
      }
      return false;
    },
    isWorker() {
      if (this.currentUser && this.currentUser.roles) {
        return this.currentUser.roles.includes(Role.Worker);
      }
      return false;
    },
    currentUser() {
      return this.$store.state.auth.user;
    }
  },
  methods: {
    showFacility(facilityId) {
      if (this.isWorker) {
        // show basic facility dialog
        this.$refs.facilityDialog.initDialog(facilityId);
      } else {
        // route to facility
        this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${facilityId}`)
      }
    },

    goToTask(task) {      
      // If task is of type result, open result dialog
      if(!task.isJob && !task.isRoleInvitation && !this.isWorker) {
        this.$refs.processResultDialog.openDialog(task)
      } else {        
        this.$refs.processShowDialog.openDialog(task)
      }
    },

    updateTaskList(options, search, filter, period) {
      this.taskList.options = options;
      this.taskList.search = search;
      this.taskList.filter = filter;
      this.taskList.period = period;

      if(filter === 'all') {
        this.readTaskList(options, search, period)
      } else if(filter === 'allInvitations') {
        this.readAllInvitations(options, search, period);
      } else if(filter === 'processInvitations') {
        this.readProcessInvitations(options, search, period)
      } else if(filter === 'roleInvitations') {
        this.readRoleInvitations(options, search)
      } else if(filter === 'assignedProcess') {
        this.readProcessAssignments(options, search, period)
      } else if(filter === 'finishedProcess') {
        this.readProcessFinished(options, search, period)
      } else if(filter === 'thisWeek') {
        this.readProcessThisWeek(options, search);
      }
    },

    readTaskList(options, search, period) {
      if(!search) search = '';

      TaskListService.readPageableCustomerTaskListFromAPI(
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        period,
        (res) => {          
          res.data.taskItems.content.forEach((task) => {
            task.taskStatus = TaskListUtils.getTaskStatus(task);
          })
          this.$refs.taskList.fillList(res.data.taskItems)
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:task-list')}))
          this.$logger.error(error);
        }
      )
    },

    readAllInvitations(options, search, period) {
      if(!search) search = '';

      TaskListService.readPageableCustomerInvitationsromAPI(
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        period,
        (res) => {
          res.data.taskItems.content.forEach((task) => {
            task.taskStatus = TaskListUtils.getTaskStatus(task);
          })
          this.$refs.taskList.fillList(res.data.taskItems)
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:task-list')}))
          this.$logger.error(error);
        }
      )
    },

    readProcessInvitations(options, search, period) {
      if(!search) search = '';

      TaskListService.readPageableCustomerProcessInvitationsFromAPI(
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        period,
        (res) => {
          res.data.taskItems.content.forEach((task) => {
            task.taskStatus = TaskListUtils.getTaskStatus(task);
          })
          this.$refs.taskList.fillList(res.data.taskItems)
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:task-list')}))
          this.$logger.error(error);
        }
      )
    },

    readRoleInvitations(options, search) {
      if(!search) search = '';

      TaskListService.readPageableCustomerRolesInvitationsFromAPI(
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        (res) => {
          res.data.taskItems.content.forEach((task) => {
            task.taskStatus = TaskListUtils.getTaskStatus(task);
          })
          this.$refs.taskList.fillList(res.data.taskItems)
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:task-list')}))
          this.$logger.error(error);
        }
      )
    },

    readProcessAssignments(options, search, period) {
      if(!search) search = '';

      TaskListService.readPageableCustomerProcessAssignmentsFromAPI(
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        period,
        (res) => {
          res.data.taskItems.content.forEach((task) => {
            task.taskStatus = TaskListUtils.getTaskStatus(task);
          })
          this.$refs.taskList.fillList(res.data.taskItems)
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:task-list')}))
          this.$logger.error(error);
        }
      )
    },

    readProcessFinished(options, search, period) {
      if(!search) search = '';

      TaskListService.readPageableCustomerProcessFinishedFromAPI(
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        period,
        (res) => {
          res.data.taskItems.content.forEach((task) => {
            task.taskStatus = TaskListUtils.getTaskStatus(task);
          })
          this.$refs.taskList.fillList(res.data.taskItems)
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:task-list')}))
          this.$logger.error(error);
        }
      )
    },

    readProcessThisWeek(options, search) {
      if(!search) search = '';

      TaskListService.readPageableCustomerProcessThisWeekFromAPI(
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        (res) => {
          res.data.taskItems.content.forEach((task) => {
            task.taskStatus = TaskListUtils.getTaskStatus(task);
          })
          this.$refs.taskList.fillList(res.data.taskItems)
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:task-list')}))
          this.$logger.error(error);
        }
      )
    },

    startProcessJob(item, gpsCheck) {
      // TODO save users decicion on GPS Check in backend
      if(gpsCheck) {
        this.acceptedGPSCheck = true;
      } else {
        this.acceptedGPSCheck = false;
      }

      ProcessJobService.startProcessJob(item.id, this.acceptedGPSCheck,
        () => {
          this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/process/${item.id}`)
        },
        (error) => {
          this.$logger.info("Error at starting Process Job: " + error);
        }
      )
    },

    showGPSPermission(item) {
      this.$refs.checkGPSPermission.showDialog = true;
      this.$refs.checkGPSPermission.propToGiveThrough = item;
      this.$store.commit("book/process/storeRideCount", null);
      this.$store.commit("book/process/storeFileNumber", null);
    },

    checkUserLocation(item) {
      FacilityService.readFromAPI(item.facility,
        (resp) => {
          var response = DataUtils.parseDataToSingleObject(resp.data, Facility);
          var buildingLat = response.buildingLatitude;
          var buildingLng = response.buildingLongitude;

          this.$getLocation({
            enableHighAccuracy: true,
          }).then(coordinates => {
              var userLat = coordinates.lat;
              var userLng = coordinates.lng;

              var distance = GPSUtils.calcDistance(userLat, userLng, buildingLat, buildingLng);
              if(distance == -1){
                Utils.showFailureSnackbar(this.$t('utils:gps.missing-value'))
              }
              if(distance < process.env.VUE_APP_CHECK_IN_LOCATION_RADIUS) {
                this.startProcessJob(item, true);
              } else {
                this.$refs.checkGPSPermission.showDialog = false;
                Utils.showFailureSnackbar(this.$t('utils:gps.distance-error'))
              }
          }).catch((error) => {
            this.$logger.info("Could not access GPS data, because: " + error);
            Utils.showFailureSnackbar(this.$t('utils:gps.access-error'))
          });
        }
      )
    },

    acceptInvitation(item) {
      if(item.isJob) {
        ProcessJobService.acceptProcessJob(item.id,
          () => {
            this.updateTaskList(this.taskList.options, this.taskList.search, this.taskList.filter, this.taskList.period);
            this.getCountOpen();
            this.getCountInvitation();
            Utils.showSuccessSnackbar(this.$t('book/process:dashboard.task-list.snackbar.accept-process'));
          },
          (error) => {
            this.$logger.info("Error at accepting Process Job: " + error);
          }
        )
      } else if(item.isRoleInvitation) {
        FacilityUserAssignmentService.acceptRoleInvitation(item.id,
          () => {
            this.updateTaskList(this.taskList.options, this.taskList.search, this.taskList.filter, this.taskList.period);
            this.getCountInvitation();
            Utils.showSuccessSnackbar(this.$t('book/process:dashboard.task-list.snackbar.accept-role'));
          },
          (error) => {
            this.$logger.error("Error at accepting Role Invitation: " + error);
          }
        )
      }
    },

    declineInvitation(item) {
      if(item.isJob) {
        ProcessJobService.rejectProcessJob(item.id,
          () => {
            this.updateTaskList(this.taskList.options, this.taskList.search, this.taskList.filter, this.taskList.period);
            this.getCountOpen();
            this.getCountInvitation();
            Utils.showSuccessSnackbar(this.$t('book/process:dashboard.task-list.snackbar.reject-process'));
          },
          (error) => {
            this.$logger.info("Error at rejecting Process Job: " + error);
          }
        )
      } else if(item.isRoleInvitation) {
        FacilityUserAssignmentService.rejectRoleInvitation(item.id,
          () => {
            this.updateTaskList(this.taskList.options, this.taskList.search, this.taskList.filter, this.taskList.period);
            this.getCountInvitation();
            Utils.showSuccessSnackbar(this.$t('book/process:dashboard.task-list.snackbar.reject-role'));
          },
          (error) => {
            this.$logger.error("Error at declining Role Invitation: " + error);
          }
        )
      }
    },

    getCountFinished() {
      TaskListService.countFinishedProcess(
        (res) => {
          this.countFinished = res.data.toString();
        }
      )
    },

    getCountOpen() {
      TaskListService.countOpenProcess(
        (res) => {
          this.countOpen = res.data.toString();
        }
      )
    },

    getCountInvitation() {
      TaskListService.countInvitations(
        (res) => {
          this.countInvitations = res.data.toString();
        }
      )
    },

    getCountThisWeek() {
      TaskListService.countThisWeek(
        (res) => {
          this.countThisWeek = res.data.toString();
        }
      )
    }
  },
  created () {
    this.currentDate = DateUtils.getCurrentDateAsDEString();

    this.getCountFinished();
    this.getCountOpen();
    this.getCountInvitation();
    this.getCountThisWeek();
  },

}

</script>
<style lang="scss">
.v-app-bar.v-app-bar--fixed {
  z-index: 5!important
}
</style>